// Knockout-bracket projection. Given the live-aware group standings and the
// knockout fixtures (which carry placeholder strings until the real draw lands),
// this resolves who *would* fill each Round-of-32 slot and how the bracket nodes
// feed forward. Pure and DB-free so web and native share it.
//
// Only the R32 entrants are projectable from standings; R16+ slots stay as their
// "Winner …" placeholders until a real result populates the match's team ids.

import { knockoutWinnerSide } from './scoring';
import {
  disciplinaryPointsForTeamInMatch,
  orderGroupStandings,
  qualifyingThirdPlaceIds,
  type H2HMatch,
} from './standings';
import { THIRD_PLACE_ALLOCATION } from './third-place-allocation';
import { isMatchLive, type MatchPublic, type MatchStage, type TeamPublic } from './types';

// Group winners that face a third-placed team in the R32, in the column order of
// the Annex C table (THIRD_PLACE_ALLOCATION values index into this). Ties the
// allocation data to the winner letter, not to repo-specific match numbers.
// Exported so the position-certainty logic (knockout-locks.ts) maps a third
// slot to its Annex-C column the same way resolveSide does.
export const WINNER_ORDER = 'ABDEGIKL';

/** A group's standings rows, ordered best-first (rows[0]=winner, [2]=third). */
export interface BracketGroup {
  group: string;
  rows: { team: TeamPublic; pts: number; gf: number; ga: number; disciplinaryPoints: number }[];
}

export interface ResolvedTeams {
  team1: TeamPublic | null;
  team2: TeamPublic | null;
}

function groupSlot(
  placeholder: string,
  byGroup: Map<string, BracketGroup['rows']>,
): TeamPublic | null {
  // "1A" = winner of A, "2B" = runner-up of B.
  const m = /^([12])([A-L])$/.exec(placeholder);
  if (!m) return null;
  const rows = byGroup.get(m[2]!);
  return rows?.[Number(m[1]) - 1]?.team ?? null;
}

/**
 * Resolve one side of a knockout match.
 * - real team already on the match → keep it (a real draw/result always wins);
 * - "1A"/"2B" → the projected group winner / runner-up;
 * - "3rd …" → the Annex-C-assigned third for THIS match's paired winner;
 * - "Winner …"/"Loser …" → null (not projectable from standings).
 */
function resolveSide(
  realTeam: TeamPublic | null,
  ownPlaceholder: string | null,
  pairedPlaceholder: string | null,
  byGroup: Map<string, BracketGroup['rows']>,
  assignment: string | null,
): TeamPublic | null {
  if (realTeam) return realTeam;
  if (!ownPlaceholder) return null;

  if (/^3rd /.test(ownPlaceholder)) {
    // The paired side is the group winner ("1X"); Annex C maps winner → third.
    const w = pairedPlaceholder ? /^1([A-L])$/.exec(pairedPlaceholder) : null;
    if (!w || !assignment) return null;
    const thirdGroup = assignment[WINNER_ORDER.indexOf(w[1]!)];
    if (!thirdGroup) return null;
    return byGroup.get(thirdGroup)?.[2]?.team ?? null;
  }

  return groupSlot(ownPlaceholder, byGroup);
}

/**
 * Project the teams for every knockout match from the current group standings.
 * Returns a map of matchId → resolved {team1, team2} (either may be null when a
 * slot can't be projected yet). `groups` is the live-aware output of buildGroups.
 */
export function resolveBracket(
  matches: MatchPublic[],
  groups: BracketGroup[],
): Map<string, ResolvedTeams> {
  const byGroup = new Map(groups.map((g) => [g.group, g.rows]));

  // The combination of groups whose third qualified → the Annex C row. The
  // value string lists the third's group per winner column, in WINNER_ORDER.
  const qualifyingIds = qualifyingThirdPlaceIds(groups);
  const comboKey = groups
    .filter((g) => g.rows[2] && qualifyingIds.has(g.rows[2]!.team.id))
    .map((g) => g.group)
    .sort()
    .join('');
  const assignment = THIRD_PLACE_ALLOCATION[comboKey] ?? null;

  const out = new Map<string, ResolvedTeams>();
  for (const m of matches) {
    if (m.stage === 'group') continue;
    out.set(m.id, {
      team1: resolveSide(m.team1, m.team1Placeholder, m.team2Placeholder, byGroup, assignment),
      team2: resolveSide(m.team2, m.team2Placeholder, m.team1Placeholder, byGroup, assignment),
    });
  }
  return out;
}

// ---------- Final standings (server-side bracket resolution) ----------

function tallyMatch(row: { pts: number; gf: number; ga: number }, gf: number, ga: number): void {
  row.gf += gf;
  row.ga += ga;
  if (gf > ga) row.pts += 3;
  else if (gf === ga) row.pts += 1;
}

/**
 * Build each group's standings from FINISHED group matches only, ordered
 * best-first via the canonical {@link orderGroupStandings}. Returns the exact
 * BracketGroup shape resolveBracket/resolveBracketFinal consume. A lean,
 * server-friendly sibling of the clients' buildGroups (which also folds in live
 * results + UI tint fields) — both funnel through orderGroupStandings, so the
 * 1st/2nd/3rd slotting can never diverge.
 */
export function buildBracketGroups(teams: TeamPublic[], matches: MatchPublic[]): BracketGroup[] {
  const teamsByGroup = new Map<string, TeamPublic[]>();
  for (const t of teams) {
    if (!t.group) continue;
    const arr = teamsByGroup.get(t.group) ?? [];
    arr.push(t);
    teamsByGroup.set(t.group, arr);
  }
  const matchesByGroup = new Map<string, MatchPublic[]>();
  for (const m of matches) {
    if (m.stage !== 'group' || m.status !== 'finished' || !m.result || !m.group) continue;
    const arr = matchesByGroup.get(m.group) ?? [];
    arr.push(m);
    matchesByGroup.set(m.group, arr);
  }

  return [...teamsByGroup.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([group, groupTeams]) => {
      const rows = new Map(
        groupTeams.map((t) => [t.id, { team: t, pts: 0, gf: 0, ga: 0, disciplinaryPoints: 0 }]),
      );
      const h2h: H2HMatch[] = [];
      for (const m of matchesByGroup.get(group) ?? []) {
        if (!m.team1 || !m.team2 || !m.result) continue;
        const r1 = rows.get(m.team1.id);
        const r2 = rows.get(m.team2.id);
        if (!r1 || !r2) continue;
        const ft = m.result.fullTime;
        tallyMatch(r1, ft.team1, ft.team2);
        tallyMatch(r2, ft.team2, ft.team1);
        h2h.push({ team1Id: m.team1.id, team2Id: m.team2.id, team1Goals: ft.team1, team2Goals: ft.team2 });
        r1.disciplinaryPoints += disciplinaryPointsForTeamInMatch(m.events.filter((e) => e.side === 'team1'));
        r2.disciplinaryPoints += disciplinaryPointsForTeamInMatch(m.events.filter((e) => e.side === 'team2'));
      }
      return { group, rows: orderGroupStandings([...rows.values()], h2h) };
    });
}

// ---------- Bracket topology (for drawing connectors) ----------

const TOKEN_TO_STAGE: Record<string, MatchStage> = {
  R32: 'round_of_32',
  R16: 'round_of_16',
  QF: 'quarter',
};
// The main bracket flow, left to right. third_place sits beside the final.
const MAIN_ROUNDS: MatchStage[] = ['round_of_32', 'round_of_16', 'quarter', 'semi', 'final'];

export interface BracketTree {
  /** Knockout rounds present, ordered R32 → Final; each match sorted by number. */
  rounds: { stage: MatchStage; matches: MatchPublic[] }[];
  /** matchId → ids of the matches that feed it (R32 nodes have none). */
  feedersOf: Map<string, string[]>;
  thirdPlace: MatchPublic | null;
}

// FIFA's canonical Round-of-32 order. The provider's "Winner R32 Match N"
// strings index into THIS sequence (N = official FIFA match 72+N), but our
// matchNumber is assigned by kickoff time and FIFA's R32 numbering is *not*
// chronological — so a match's matchNumber ordinal is NOT its FIFA game N. We
// therefore identify each R32 match by its draw-independent group slots. Each
// entry is the sorted group-position key of game i+1; a winner-vs-third game is
// keyed by its winner alone (the third is draw-dependent, not identifying).
// Lines up with the winner→third candidate sets in knockout.test.ts (Annex C).
const R32_FIFA_ORDER = [
  '2A|2B',
  '1E',
  '1F|2C',
  '1C|2F',
  '1I',
  '2E|2I',
  '1A',
  '1L',
  '1D',
  '1G',
  '2K|2L',
  '1H|2J',
  '1B',
  '1J|2H',
  '1K',
  '2D|2G',
];
const R32_KEY_TO_FIFA_INDEX = new Map(R32_FIFA_ORDER.map((k, i) => [k, i + 1]));

/**
 * The group-slot key of an R32 match ("2A|2B", "1F|2C", or just "1E" for a
 * winner-vs-third game), or null if a side can't be classified. Reads the
 * placeholder strings before kickoff and the resolved teams' standings slot
 * after — placeholders are cleared once a match gets real teams — so the key is
 * stable across the whole knockout phase.
 */
function r32SlotKey(m: MatchPublic, slotOfTeam: (t: TeamPublic) => string | null): string | null {
  const tokens: string[] = [];
  for (const [ph, real] of [
    [m.team1Placeholder, m.team1],
    [m.team2Placeholder, m.team2],
  ] as [string | null, TeamPublic | null][]) {
    let slot: string | null = null;
    if (ph) {
      if (/^3rd /.test(ph))
        slot = '3'; // a third-placed slot — drops out of the key
      else if (/^[12][A-L]$/.test(ph)) slot = ph;
      else return null; // a "Winner …" ref on an R32 match: not expected
    } else if (real) {
      slot = slotOfTeam(real);
    }
    if (slot === null) return null; // a side we can't classify yet → no key
    if (!slot.startsWith('3')) tokens.push(slot);
  }
  return tokens.length ? tokens.sort().join('|') : null;
}

interface FeederRef {
  matchId: string;
  /** Whether the winner or the loser of the feeder advances into this slot. */
  role: 'winner' | 'loser';
}

/**
 * Resolve a "Winner/Loser …" placeholder to the feeder match it references and
 * which side (winner/loser) advances. R16/QF/SF/final use each round's
 * matchNumber order; R32 is the exception — "Winner R32 Match N" uses FIFA's
 * non-chronological game numbering, resolved via the ordinalId map's group-slot
 * keys. Returns null for group-position placeholders ("1A"/"3rd …") and unknown
 * refs.
 */
function feederRef(placeholder: string | null, ordinalId: Map<string, string>): FeederRef | null {
  if (!placeholder) return null;
  let m = /^(Winner|Loser) (R32|R16|QF) Match (\d+)$/.exec(placeholder);
  if (m) {
    const id = ordinalId.get(`${TOKEN_TO_STAGE[m[2]!]}#${Number(m[3])}`);
    return id ? { matchId: id, role: m[1] === 'Loser' ? 'loser' : 'winner' } : null;
  }
  m = /^(Winner|Loser) SF(\d+)$/.exec(placeholder);
  if (m) {
    const id = ordinalId.get(`semi#${Number(m[2])}`);
    return id ? { matchId: id, role: m[1] === 'Loser' ? 'loser' : 'winner' } : null;
  }
  return null;
}

/**
 * Build the (stage, 1-based ordinal) → matchId index plus the per-stage match
 * lists (sorted by matchNumber). R16/QF/SF/final keep the repo's matchNumber
 * order (which agrees with FIFA there); R32 is keyed by FIFA game index from
 * each match's group slots — see R32_FIFA_ORDER. `groups` (live-aware standings)
 * lets a resolved R32 match be re-keyed after its placeholders are replaced by
 * real teams. Shared by buildBracketTree and resolveBracketFinal.
 */
function buildOrdinalId(
  matches: MatchPublic[],
  groups: BracketGroup[],
): { ordinalId: Map<string, string>; byStage: Map<MatchStage, MatchPublic[]> } {
  const byStage = new Map<MatchStage, MatchPublic[]>();
  for (const m of matches) {
    if (m.stage === 'group') continue;
    const arr = byStage.get(m.stage) ?? [];
    arr.push(m);
    byStage.set(m.stage, arr);
  }

  // A resolved knockout team's group-finish slot ("1A"/"2A"/"3A"), used to
  // re-identify an R32 match once its placeholders have been replaced by real
  // teams.
  const slotByTeamId = new Map<string, string>();
  for (const g of groups) {
    g.rows.forEach((row, i) => {
      if (i < 3) slotByTeamId.set(row.team.id, `${i + 1}${g.group}`);
    });
  }
  const slotOfTeam = (t: TeamPublic): string | null => slotByTeamId.get(t.id) ?? null;

  const ordinalId = new Map<string, string>();
  for (const [stage, arr] of byStage) {
    arr.sort((a, b) => a.matchNumber - b.matchNumber);
    if (stage === 'round_of_32') {
      for (const m of arr) {
        const key = r32SlotKey(m, slotOfTeam);
        const idx = key ? R32_KEY_TO_FIFA_INDEX.get(key) : undefined;
        if (idx) ordinalId.set(`round_of_32#${idx}`, m.id);
      }
    } else {
      arr.forEach((m, i) => ordinalId.set(`${stage}#${i + 1}`, m.id));
    }
  }

  return { ordinalId, byStage };
}

/**
 * Derive the feed-forward topology from the placeholder strings, resilient to
 * the repo's chronological match numbering. See buildOrdinalId / feederRef for
 * the placeholder grammars and the R32 FIFA-numbering exception.
 */
export function buildBracketTree(matches: MatchPublic[], groups: BracketGroup[]): BracketTree {
  const { ordinalId, byStage } = buildOrdinalId(matches, groups);

  const feedersOf = new Map<string, string[]>();
  for (const arr of byStage.values()) {
    for (const m of arr) {
      feedersOf.set(
        m.id,
        [feederRef(m.team1Placeholder, ordinalId), feederRef(m.team2Placeholder, ordinalId)]
          .map((ref) => ref?.matchId ?? null)
          .filter((x): x is string => x !== null),
      );
    }
  }

  const rounds = MAIN_ROUNDS.map((stage) => ({
    stage,
    matches: byStage.get(stage) ?? [],
  })).filter((r) => r.matches.length > 0);

  return { rounds, feedersOf, thirdPlace: byStage.get('third_place')?.[0] ?? null };
}

/**
 * The "certain" counterpart to resolveBracket: returns each knockout match's
 * teams ONLY where they're now finally determined, else null. Used server-side
 * to persist participants as matches finish.
 *
 *   - real team already on the match → keep it;
 *   - "1A"/"2B" → final group winner/runner-up, once that group's matches are
 *     ALL finished (intra-group order can't change after that);
 *   - "3rd …" → the Annex-C third, once EVERY group is finished (the qualifying
 *     set + allocation depend on all groups);
 *   - "Winner/Loser …" → the winner/loser of the feeder, once it's finished.
 *
 * Pull-based + null-until-certain, so a caller that writes only non-null slots
 * is idempotent and cascades over successive runs.
 */
export function resolveBracketFinal(
  matches: MatchPublic[],
  groups: BracketGroup[],
): Map<string, ResolvedTeams> {
  const projected = resolveBracket(matches, groups);
  const byId = new Map(matches.map((m) => [m.id, m]));
  const { ordinalId } = buildOrdinalId(matches, groups);

  // A group is complete once it has matches and none are still unfinished. The
  // server loads ALL matches, so "no group has an unfinished match" is exactly
  // "the group stage is over" (which the thirds + Annex-C allocation need).
  const groupsWithMatches = new Set<string>();
  const incompleteGroups = new Set<string>();
  for (const m of matches) {
    if (m.stage !== 'group' || !m.group) continue;
    groupsWithMatches.add(m.group);
    if (m.status !== 'finished') incompleteGroups.add(m.group);
  }
  const isGroupComplete = (letter: string): boolean =>
    groupsWithMatches.has(letter) && !incompleteGroups.has(letter);
  const allGroupsComplete = groupsWithMatches.size > 0 && incompleteGroups.size === 0;

  const finalSide = (
    real: TeamPublic | null,
    placeholder: string | null,
    projectedTeam: TeamPublic | null,
  ): TeamPublic | null => {
    if (real) return real;
    if (!placeholder) return null;

    const g = /^[12]([A-L])$/.exec(placeholder);
    if (g) return isGroupComplete(g[1]!) ? projectedTeam : null;
    if (/^3rd /.test(placeholder)) return allGroupsComplete ? projectedTeam : null;

    const ref = feederRef(placeholder, ordinalId);
    if (!ref) return null;
    const feeder = byId.get(ref.matchId);
    if (!feeder || feeder.status !== 'finished' || !feeder.team1 || !feeder.team2 || !feeder.result) {
      return null;
    }
    const winSide = knockoutWinnerSide(feeder.result);
    if (!winSide) return null;
    const winner = winSide === 'team1' ? feeder.team1 : feeder.team2;
    const loser = winSide === 'team1' ? feeder.team2 : feeder.team1;
    return ref.role === 'loser' ? loser : winner;
  };

  const out = new Map<string, ResolvedTeams>();
  for (const m of matches) {
    if (m.stage === 'group') continue;
    const proj = projected.get(m.id);
    out.set(m.id, {
      team1: finalSide(m.team1, m.team1Placeholder, proj?.team1 ?? null),
      team2: finalSide(m.team2, m.team2Placeholder, proj?.team2 ?? null),
    });
  }
  return out;
}

// ---------- Live-slot classification (the per-slot "spine") ----------

export type LiveSlot =
  | { state: 'none' }
  | { state: 'own' }
  | { state: 'elsewhere'; match: MatchPublic };

/** Index of live matches by each participating team's id. */
export function liveMatchesByTeam(matches: MatchPublic[]): Map<string, MatchPublic> {
  const map = new Map<string, MatchPublic>();
  for (const m of matches) {
    if (!isMatchLive(m)) continue;
    if (m.team1) map.set(m.team1.id, m);
    if (m.team2) map.set(m.team2.id, m);
  }
  return map;
}

/**
 * For a team occupying a bracket slot whose node is `ownMatchId`: is it idle,
 * live in this very node's match ('own' → show the node's score centrally), or
 * live in a *different* (group) game ('elsewhere' → show that game on the slot)?
 */
export function classifyLiveSlot(
  team: TeamPublic | null,
  ownMatchId: string,
  liveByTeamId: Map<string, MatchPublic>,
): LiveSlot {
  if (!team) return { state: 'none' };
  const live = liveByTeamId.get(team.id);
  if (!live) return { state: 'none' };
  if (live.id === ownMatchId) return { state: 'own' };
  return { state: 'elsewhere', match: live };
}
